import React, { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import { X, HelpCircle } from 'lucide-react';

// v2.4 QoL — one-time first-run onboarding tooltips. Once dismissed, never shows again for that tag.

const STEPS = [
  { screen: 'menu', body: 'Type a TAG and hit PLAY to start your first run. Aim for the final boss on floor 20.' },
  { screen: 'combat', body: 'Click cards or press 1-9 to play. Watch enemy INTENT icons — block or attack accordingly. Press ? for hotkeys.' },
  { screen: 'map', body: 'Choose your path. Elites give better loot but hurt more. Shrines and treasures are free wins.' },
  { screen: 'reward', body: 'Pick 1 card (or skip). Curses live in your deck — remove them at shops or shrines.' },
  { screen: 'shop', body: 'Buy cards, relics, or REROLL the shelf for gold. Card removal cleans up your deck.' },
];

export default function OnboardingHints() {
  const { screen, playerName } = useGame();
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('friendslop.onboarded') || '{}'); } catch (e) { return {}; }
  });
  const [visible, setVisible] = useState(false);

  const step = STEPS.find(s => s.screen === screen);

  useEffect(() => {
    if (!step) { setVisible(false); return; }
    if (dismissed[step.screen]) { setVisible(false); return; }
    // Small delay so it doesn't pop over transitions
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [step, dismissed]);

  if (!step || !visible) return null;

  const dismiss = () => {
    const next = { ...dismissed, [step.screen]: 1 };
    setDismissed(next);
    setVisible(false);
    try { localStorage.setItem('friendslop.onboarded', JSON.stringify(next)); } catch (e) {}
  };
  const dismissAll = () => {
    const next = STEPS.reduce((acc, s) => ({ ...acc, [s.screen]: 1 }), {});
    setDismissed(next);
    setVisible(false);
    try { localStorage.setItem('friendslop.onboarded', JSON.stringify(next)); } catch (e) {}
  };

  return (
    <div
      className="fixed bottom-32 right-4 z-50 max-w-xs brutal-border p-3 bg-[#0a0a0a] relative"
      style={{ borderColor: 'var(--neon)', boxShadow: '4px 4px 0 var(--neon)' }}
      data-testid="onboarding-tip"
      data-screen={step.screen}
    >
      <button
        onClick={dismiss}
        className="absolute top-1 right-1 text-[color:var(--text-dim)] hover:text-white"
        data-testid="onboarding-dismiss"
        aria-label="close hint"
      >
        <X size={12}/>
      </button>
      <div className="flex items-start gap-2">
        <HelpCircle size={14} className="text-[color:var(--neon)] mt-0.5 shrink-0"/>
        <div className="text-xs leading-relaxed text-[color:var(--text-dim)]" data-testid="onboarding-body">
          {step.body}
        </div>
      </div>
      <button
        onClick={dismissAll}
        className="mt-2 text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] hover:text-[color:var(--pink)]"
        data-testid="onboarding-dismiss-all"
      >
        DON'T SHOW HINTS AGAIN
      </button>
    </div>
  );
}
